import React, { useEffect, useState } from 'react';
import { OutcomeInfo } from '../../../models/APIModels';
import { cloneDeep } from 'lodash';
import { Grid, Box, TextField, FormControlLabel, Switch } from '@mui/material';
import AutoComplete from '../../AutoComplete/AutoCompleteSelect';
import { dataTypes } from '../AIEngineConfiguration';
import clientUtils from '../../../Utils/clientUtils';

interface IProps {
  selectedOutcome: OutcomeInfo;
  outcomes: OutcomeInfo[];
  updateOutcomeConfiguration: (outcomes: OutcomeInfo[], selectedOutcome: OutcomeInfo) => void;
  dataTypes: any[];
  disabled?: boolean;
}

const OutcomeConfiguration = ({
  selectedOutcome,
  outcomes,
  updateOutcomeConfiguration,
  configureVariableDisplayOptions,
  dataTypes,
  disabled = false,
}) => {
  // ➡️ State Variables .....

  const [selectedType, setSelectedType] = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [selectedOutputDescription, setSelectedOutputDescription] = useState('');
  const [toggleBtn, setToggleBtn] = useState(false);
  const [multilineToggleBtn, setMultilineToggleBtn] = useState(false);
  const [previewImageToggleBtn, setPreviewImageToggleBtn] = useState(false);

  // ➡️ Functions/Methods .....

  const onOutcomeTextChange = (event, field) => {
    // console.log(event.target.value);
    const value = event.target.value;
    const cloneOutcomes = cloneDeep(outcomes);
    const index = cloneOutcomes.findIndex((i) => i.name === selectedOutcome.name);
    if (index !== -1) {
      const input = cloneOutcomes[index];
      if (field === 'name') {
        const cloneDisplaySettings = configureVariableDisplayOptions('view');
        const keyName = clientUtils.uniqueKeyNameGenForVariableDisplayOptions(input[field], 'outputcon');
        let DisplaySettingsFlagValue = { enableMultiLineText: false, enablePictureViewer: false };
        if (keyName in cloneDisplaySettings) {
          DisplaySettingsFlagValue = cloneDisplaySettings[keyName];
          configureVariableDisplayOptions('pop', { name: input[field] });
          input[field] = value;

          configureVariableDisplayOptions('push', { name: input[field] });
          if (DisplaySettingsFlagValue.enableMultiLineText) {
            configureVariableDisplayOptions('setflag', { name: input[field] }, 'multiline');
          }
          if (DisplaySettingsFlagValue.enablePictureViewer) {
            configureVariableDisplayOptions('setflag', { name: input[field] }, 'imgPreview');
          }
        } else {
          input[field] = value;
        }
        setSelectedName(value);
      } else if (field === 'outcomeDescription') {
        input[field] = value;
        setSelectedOutputDescription(value);
      }
      updateOutcomeConfiguration(cloneOutcomes, input);
      // setInputs(inputs);
      // setSelectedInput(input);
    }
  };

  // Function/Method :: Handels Input Type of Outcomes .....
  const handleOutcomeType = (value) => {
    const cloneOutcomes = cloneDeep(outcomes);
    if (selectedOutcome) {
      const index = cloneOutcomes.findIndex((i) => i.name === selectedOutcome.name);
      if (index !== -1) {
        const outcome = cloneOutcomes[index];
        outcome.outcomeType = value.name;
        updateOutcomeConfiguration(cloneOutcomes, outcome);
      }
    }
  };

  // ➡️ Hooks .....

  useEffect(() => {
    // console.log('selectedOutcome changed', selectedOutcome)
    if (selectedOutcome) {
      setSelectedName(selectedOutcome.name);
      setSelectedOutputDescription(selectedOutcome.outcomeDescription);
      const outcomeType = dataTypes.find((i) => i.name === selectedOutcome.outcomeType);
      if (outcomeType) {
        setSelectedType(outcomeType);
      }

      // prefill Multiline Display .....
      const variableDisplayClone = configureVariableDisplayOptions('view');
      const displayKeyName = clientUtils.uniqueKeyNameGenForVariableDisplayOptions(selectedOutcome.name, 'outputcon');
      const enableMultiLineText = variableDisplayClone[displayKeyName]?.enableMultiLineText ?? false;
      const enablePictureViewer = variableDisplayClone[displayKeyName]?.enablePictureViewer ?? false;
      setMultilineToggleBtn(enableMultiLineText);
      setPreviewImageToggleBtn(enablePictureViewer);
    }
  }, [selectedOutcome]);

  useEffect(() => {
    if (selectedOutcome) {
      if (multilineToggleBtn) {
        configureVariableDisplayOptions('push', selectedOutcome);
        configureVariableDisplayOptions('setflag', selectedOutcome, 'multiline');
      } else {
        setPreviewImageToggleBtn(false)
        configureVariableDisplayOptions('setflagFalse', selectedOutcome);
      }
    }
  }, [multilineToggleBtn]);

  useEffect(() => {
    if (selectedOutcome) {
      if (previewImageToggleBtn) {
        configureVariableDisplayOptions('push', selectedOutcome);
        configureVariableDisplayOptions('setflag', selectedOutcome, 'imgPreview');
      } else {
        setMultilineToggleBtn(false)
        configureVariableDisplayOptions('setflagFalseImgPrev', selectedOutcome);
      }
    }
  }, [previewImageToggleBtn]);

  // ➡️ Debug Code's .....
  // useEffect(() => {
  // console.log(outcomes)
  // }, [outcomes])

  return (
    <Grid container spacing={1} direction="row">
      <Grid item xs={12}>
        <Box className="hedading">
          <div className="title">Outcome Configuration</div>
          <div className="line"></div>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <TextField
          id="name"
          label="Name"
          variant="outlined"
          placeholder=""
          size="small"
          fullWidth
          onChange={(e) => onOutcomeTextChange(e, 'name')}
          value={selectedName}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          id="outcomeDescription"
          label="Outcome Description"
          variant="outlined"
          placeholder=""
          size="small"
          fullWidth
          onChange={(e) => onOutcomeTextChange(e, 'outcomeDescription')}
          value={selectedOutputDescription}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <AutoComplete
          id="input-type-select"
          items={dataTypes}
          itemText="name"
          itemKey="id"
          labelText="Outcome Type"
          placeHolderText="Select Outcome Type"
          multiSelect={false}
          onChange={handleOutcomeType}
          defaultValue={selectedType}
          disabled={disabled}
        />
      </Grid>

      {/* Multiiline Toggle */}
      {selectedType.name == 'String' && !disabled && (
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <FormControlLabel
            sx={{ ml: '2px' }}
            control={
              <div style={{ display: 'flex', marginLeft: '5px' }}>
                <Switch size="small" checked={multilineToggleBtn} color={'info'} />
              </div>
            }
            onChange={(e) => {
              setMultilineToggleBtn(e.target.checked);
            }}
            label="Enable multi-line"
            labelPlacement="start"
          />
        </Grid>
      )}

      {/* enablePictureViewer Toggle */}
      {selectedType?.name == 'File' && !disabled && (
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <FormControlLabel
            sx={{ ml: '2px' }}
            control={
              <div style={{ display: 'flex', marginLeft: '5px' }}>
                <Switch size="small" checked={previewImageToggleBtn} color={'info'} />
              </div>
            }
            onChange={(e) => {
              setPreviewImageToggleBtn(e.target.checked);
            }}
            label="Enable Picture Preview"
            labelPlacement="start"
          />
        </Grid>
      )}
    </Grid>
  );
};

export default OutcomeConfiguration;
